// src/pages/Consultation/modals/CompleteSessionModal.jsx
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiX,
  FiCheckCircle,
  FiXCircle,
  FiStar,
  FiCalendar,
  FiShoppingCart,
} from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "../../../../components/common/Button";
import { completeSessionThunk } from "../../../../store/thunks/consultationThunks";

const NO_PURCHASE_REASONS = [
  { value: "PRICE_TOO_HIGH", label: "Price Too High" },
  { value: "PRODUCT_NOT_FOUND", label: "Desired Product Not Available" },
  { value: "NEEDS_MORE_TIME", label: "Needs More Time to Decide" },
  { value: "COMPARING_OPTIONS", label: "Comparing with Other Options" },
  { value: "BUDGET_CONSTRAINT", label: "Budget Constraint" },
  { value: "QUALITY_CONCERN", label: "Quality Concerns" },
  { value: "JUST_BROWSING", label: "Just Browsing" },
  { value: "WILL_RETURN_LATER", label: "Will Return Later" },
  { value: "OTHER", label: "Other" },
];

const CompleteSessionModal = ({
  isOpen,
  onClose,
  session,
  adminInfo,
  hasCart,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    outcome: "", // "CONVERTED" | "NO_PURCHASE"
    noPurchaseReason: "",
    noPurchaseDetails: "",
    followUpRequired: false,
    followUpDate: "",
    followUpNotes: "",
    customerRating: 0,
    customerFeedback: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Handle field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle star rating
  const handleRatingClick = (rating) => {
    setFormData((prev) => ({ ...prev, customerRating: rating }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.outcome) {
      newErrors.outcome = "Please select an outcome";
    }

    if (formData.outcome === "NO_PURCHASE" && !formData.noPurchaseReason) {
      newErrors.noPurchaseReason = "Please select a reason";
    }

    if (
      formData.noPurchaseReason === "OTHER" &&
      !formData.noPurchaseDetails.trim()
    ) {
      newErrors.noPurchaseDetails = "Please specify the reason";
    }

    if (formData.followUpRequired && !formData.followUpDate) {
      newErrors.followUpDate = "Please select a follow-up date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    // If converted, redirect to order creation instead
    if (formData.outcome === "CONVERTED") {
      if (!hasCart) {
        toast.error("Cannot create order - session has no cart");
        return;
      }

      toast.success("Redirecting to order creation...");
      handleClose();
      navigate(`/consultation/sessions/${session._id}/create-order`);
      return;
    }

    setSubmitting(true);

    try {
      const outcomeData = {
        sessionId: session._id,
        outcomeData: {
          noPurchaseReason: formData.noPurchaseReason,
          noPurchaseDetails:
            formData.noPurchaseReason === "OTHER"
              ? formData.noPurchaseDetails
              : NO_PURCHASE_REASONS.find(
                  (r) => r.value === formData.noPurchaseReason
                )?.label,
          followUpRequired: formData.followUpRequired,
          followUpDate: formData.followUpDate || undefined,
          followUpNotes: formData.followUpNotes || undefined,
          customerRating: formData.customerRating || undefined,
          customerFeedback: formData.customerFeedback || undefined,
        },
        adminCode: adminInfo.adminCode,
        adminName: adminInfo.adminName,
      };

      await dispatch(completeSessionThunk(outcomeData)).unwrap();

      toast.success("Session completed successfully");
      handleClose();

      // Navigate back to dashboard
      navigate("/consultation");
    } catch (error) {
      console.error("Complete session error:", error);
      toast.error(error || "Failed to complete session");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!submitting) {
      setFormData({
        outcome: "",
        noPurchaseReason: "",
        noPurchaseDetails: "",
        followUpRequired: false,
        followUpDate: "",
        followUpNotes: "",
        customerRating: 0,
        customerFeedback: "",
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b z-10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success-light rounded-lg">
                <FiCheckCircle className="text-xl text-success-dark" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Complete Session
                </h3>
                <p className="text-sm text-gray-500">
                  Session: {session?.sessionCode}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <FiX className="text-xl text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Outcome Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Session Outcome *
              </label>
              <div className="grid grid-cols-2 gap-4">
                {/* Converted */}
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, outcome: "CONVERTED" }))
                  }
                  disabled={submitting || !hasCart}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    formData.outcome === "CONVERTED"
                      ? "border-success bg-success-light"
                      : "border-gray-300 hover:border-success"
                  } ${!hasCart ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <FiShoppingCart className="text-2xl mx-auto mb-2 text-success-dark" />
                  <p className="font-medium text-gray-900">Converted</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Customer made purchase
                  </p>
                  {!hasCart && (
                    <p className="text-xs text-red-500 mt-1">No cart items</p>
                  )}
                </button>

                {/* No Purchase */}
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      outcome: "NO_PURCHASE",
                    }))
                  }
                  disabled={submitting}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    formData.outcome === "NO_PURCHASE"
                      ? "border-danger bg-danger-light"
                      : "border-gray-300 hover:border-danger"
                  }`}
                >
                  <FiXCircle className="text-2xl mx-auto mb-2 text-danger-dark" />
                  <p className="font-medium text-gray-900">No Purchase</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Customer did not purchase
                  </p>
                </button>
              </div>
              {errors.outcome && (
                <p className="mt-2 text-sm text-red-500">{errors.outcome}</p>
              )}
            </div>

            {/* No Purchase Details */}
            {formData.outcome === "NO_PURCHASE" && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900">
                  No Purchase Details
                </h4>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason *
                  </label>
                  <select
                    name="noPurchaseReason"
                    value={formData.noPurchaseReason}
                    onChange={handleChange}
                    disabled={submitting}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors.noPurchaseReason
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  >
                    <option value="">Select a reason</option>
                    {NO_PURCHASE_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                  {errors.noPurchaseReason && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.noPurchaseReason}
                    </p>
                  )}
                </div>

                {/* Custom Details */}
                {formData.noPurchaseReason === "OTHER" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Details *
                    </label>
                    <textarea
                      name="noPurchaseDetails"
                      value={formData.noPurchaseDetails}
                      onChange={handleChange}
                      disabled={submitting}
                      rows={3}
                      placeholder="Explain the reason..."
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none ${
                        errors.noPurchaseDetails
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.noPurchaseDetails && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.noPurchaseDetails}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Follow-up Planning */}
            <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <FiCalendar className="text-blue-600" />
                  Follow-up Planning
                </h4>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="followUpRequired"
                    checked={formData.followUpRequired}
                    onChange={handleChange}
                    disabled={submitting}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Requires Follow-up
                  </span>
                </label>
              </div>

              {formData.followUpRequired && (
                <div className="space-y-3">
                  {/* Follow-up Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Follow-up Date *
                    </label>
                    <input
                      type="date"
                      name="followUpDate"
                      value={formData.followUpDate}
                      onChange={handleChange}
                      disabled={submitting}
                      min={new Date().toISOString().split("T")[0]}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                        errors.followUpDate
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.followUpDate && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.followUpDate}
                      </p>
                    )}
                  </div>

                  {/* Follow-up Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Follow-up Notes
                    </label>
                    <textarea
                      name="followUpNotes"
                      value={formData.followUpNotes}
                      onChange={handleChange}
                      disabled={submitting}
                      rows={3}
                      placeholder="What to discuss in follow-up..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Customer Satisfaction */}
            <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-gray-900">
                Customer Satisfaction (Optional)
              </h4>

              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingClick(star)}
                      disabled={submitting}
                      className="transition-all"
                    >
                      <FiStar
                        className={`text-3xl ${
                          star <= formData.customerRating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Feedback */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Feedback
                </label>
                <textarea
                  name="customerFeedback"
                  value={formData.customerFeedback}
                  onChange={handleChange}
                  disabled={submitting}
                  rows={3}
                  placeholder="Customer's feedback about the experience..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
            </div>

            {/* Info Box */}
            {formData.outcome === "CONVERTED" && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  You will be redirected to create an order from this session
                  after clicking Complete.
                </p>
              </div>
            )}
          </form>

          {/* Footer Actions */}
          <div className="sticky bottom-0 bg-white flex items-center justify-end gap-3 px-6 py-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
              className="bg-success hover:bg-success-dark"
            >
              {submitting
                ? "Completing..."
                : formData.outcome === "CONVERTED"
                ? "Complete & Create Order"
                : "Complete Session"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompleteSessionModal;
